import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Divider, Grid } from "@mui/material";
import Typography from "@mui/material/Typography/Typography";
import { green, yellow } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, useContext, useState } from "react";
import { routes } from "schemas/routes";

import { Template } from "features/Influencer/ContactList/schema";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

interface Props {
  editTemplate: Template;
  setEditTemplate: Dispatch<React.SetStateAction<Template>>;
  followUpTemplate?: Template;
  setFollowUpTemplate?: Dispatch<React.SetStateAction<Template>>;
  handleBack: () => void;
  handleNext: () => void;
  allowChangeTitle?: boolean;
}
const ReviewBentoAiTemplate = ({
  editTemplate,
  setEditTemplate,
  handleBack,
  handleNext,
  followUpTemplate,
  setFollowUpTemplate,
  allowChangeTitle = false,
}: Props) => {
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);

  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const isVaOnboarding = isPage(routes.templatesSelfServeOnboarding);

  const handleSaveTemplate = async () => {
    try {
      setSaveLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${editTemplate.id}`,
        "PUT",
        {},
        {
          ...editTemplate,
          validateBulkSendVariables: Boolean(isVaOnboarding),
        },
      );
      setEditTemplate(res.template);

      if (followUpTemplate && followUpTemplate?.id) {
        const { template: followup } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/templates/${followUpTemplate?.id}`,
          "PUT",
          {},
          { ...followUpTemplate },
        );
        if (setFollowUpTemplate) setFollowUpTemplate(followup);
      }
      if (handleNext) {
        handleNext();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center" }}>
        <Box
          component="i"
          color="secondary.dark"
          sx={{ pr: 2 }}
          className="fa-solid fa-paper-plane"
        />
        Review Your Template
      </Typography>
      <>
        <Typography>
          Make any edits, then click 'Save Template' and we'll save it to your
          dashboard. You can use this template to email brands faster and
          easier. Things to keep in mind before saving:
        </Typography>
        <ul>
          <li style={{ listStyleType: "none" }}>
            <Typography>
              <Box
                component="i"
                className="fa-solid fa-pen-nib"
                sx={{ pr: 1, color: green[700] }}
              />
              Review the tone, details, and add anything you'd like before
              saving.
            </Typography>
          </li>
          <li style={{ listStyleType: "none" }}>
            <Typography>
              <Box
                component="i"
                className="fa-solid fa-sparkles"
                sx={{ pr: 1, color: yellow[700] }}
              />
              Keep {"{Brand Name}"} and {"{First Name}"} as placeholders—this
              ensures each email is personalized for the brand and recipient.
            </Typography>
          </li>
          {isVaOnboarding ? (
            <li style={{ listStyleType: "none" }}>
              <Typography>
                🚨 Make sure your templates <strong>do not contain</strong> any
                other variables beside {"{Brand Name}"} and {"{First Name}"}.
              </Typography>
            </li>
          ) : (
            <li style={{ listStyleType: "none" }}>
              <Typography>
                <Box
                  component="i"
                  className="fa-solid fa-file-pen"
                  sx={{ pr: 1, color: "primary.main" }}
                />
                At any time, you can modify or create new templates by clicking
                the dropdown button in the top right corner and selecting
                "Templates."
              </Typography>
            </li>
          )}
        </ul>
        <Divider sx={{ my: 3 }} />
      </>
      <TemplateBody
        editTemplate={editTemplate}
        setEditTemplate={setEditTemplate}
        isNew={true}
        allowChangeTitle={allowChangeTitle}
        minimalist
        displayError={true}
      />

      {followUpTemplate && setFollowUpTemplate && (
        <TemplateBody
          editTemplate={followUpTemplate}
          setEditTemplate={setFollowUpTemplate}
          isNew={true}
          allowChangeTitle={allowChangeTitle}
          minimalist
          displayError={true}
        />
      )}
      <Grid item xs="auto" container columnGap={1}>
        <Grid item xs>
          <LoadingButton
            disableElevation
            sx={{ textTransform: "none" }}
            onClick={handleBack}
          >
            <Box
              component={"i"}
              className="fa-solid fa-arrow-left"
              sx={{ mr: 1 }}
            />{" "}
            Back
          </LoadingButton>
        </Grid>

        <Grid item xs="auto">
          <LoadingButton
            disableElevation
            variant="contained"
            type="submit"
            onClick={handleSaveTemplate}
            loading={saveLoading}
            sx={{ textTransform: "none" }}
          >
            Next{" "}
            <Box
              component={"i"}
              className="fa-solid fa-arrow-right"
              sx={{ ml: 1 }}
            />
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
};

export default ReviewBentoAiTemplate;
